import React from 'react';
import { 
  Alert, 
  AlertTitle, 
  Button, 
  Box, 
  Typography, 
  LinearProgress,
  Collapse,
  IconButton
} from '@mui/material';
import { 
  CloudOff, 
  Refresh, 
  Home, 
  Close, 
  ExpandMore, 
  ExpandLess,
  NetworkCheck
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useErrorHandler } from '../../stores/errorStore';
import { log } from '../../utils/errors/logger';
import { formatTime } from '../../utils/formatting/formatters';

interface BackendOfflineBannerProps {
  onRetry?: () => void;
  onDismiss?: () => void;
  showRetryProgress?: boolean;
}

export const BackendOfflineBanner: React.FC<BackendOfflineBannerProps> = ({
  onRetry,
  onDismiss,
  showRetryProgress = false
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const errorHandler = useErrorHandler();
  const [expanded, setExpanded] = React.useState(false);
  const [retryLoading, setRetryLoading] = React.useState(false);

  // Don't show if backend is online or not showing banner
  if (!errorHandler.showOfflineBanner || !errorHandler.isBackendOffline) {
    return null;
  }

  const handleRetry = async () => {
    setRetryLoading(true);
    
    try {
      const isOnline = await errorHandler.checkHealth();
      
      if (isOnline) {
        log.user.action('backend_reconnected', 'BackendOfflineBanner');
        errorHandler.dismissOfflineBanner();
        
        if (onRetry) {
          onRetry();
        }
      } else {
        log.user.action('retry_failed', 'BackendOfflineBanner');
      }
    } catch (error) {
      log.error('Health check failed', { error });
    } finally {
      setRetryLoading(false);
    }
  };

  const handleGoHome = () => {
    log.user.navigation('offline_redirect_home', { source: 'BackendOfflineBanner' });
    navigate('/', { 
      state: { 
        message: t('error.offline.redirectMessage', 'You were redirected due to server unavailability') 
      } 
    });
  };

  const handleDismiss = () => {
    errorHandler.dismissOfflineBanner();
    
    if (onDismiss) {
      onDismiss();
    }
    
    log.user.action('dismiss_offline_banner', 'BackendOfflineBanner');
  };

  return (
    <Alert
      severity="error"
      sx={{
        borderRadius: 0,
        '& .MuiAlert-message': {
          width: '100%'
        }
      }}
      action={
        <IconButton
          aria-label={t('error.offline.close', 'Close')}
          color="inherit"
          size="small"
          onClick={handleDismiss}
        >
          <Close fontSize="inherit" />
        </IconButton>
      }
    >
      <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudOff fontSize="small" />
        {t('error.offline.title', 'Server Unavailable')}
      </AlertTitle>

      <Typography variant="body2" gutterBottom>
        {t('error.offline.message', 'The server is currently unavailable. Some features may not work properly.')}
      </Typography>

      {showRetryProgress && retryLoading && (
        <Box sx={{ width: '100%', marginBottom: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="textSecondary">
            {t('error.offline.checking', 'Checking connection...')}
          </Typography>
        </Box>
      )}

      <Box sx={{ 
        display: 'flex', 
        gap: 1, 
        marginTop: 1, 
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={retryLoading ? <NetworkCheck /> : <Refresh />}
          onClick={handleRetry}
          disabled={retryLoading}
          color="inherit"
        >
          {retryLoading 
            ? t('error.offline.checking', 'Checking...')
            : t('error.offline.retry', 'Retry Connection')
          }
        </Button>

        <Button
          size="small"
          variant="text"
          startIcon={<Home />}
          onClick={handleGoHome}
          color="inherit"
        >
          {t('error.offline.home', 'Go to Home')}
        </Button>

        {process.env.NODE_ENV === 'development' && (
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            color="inherit"
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </Box>

      {process.env.NODE_ENV === 'development' && (
        <Collapse in={expanded}>
          <Box sx={{ 
            marginTop: 2, 
            padding: 1, 
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderRadius: 1
          }}>
            <Typography variant="caption" component="div">
              <strong>Debug Info:</strong>
            </Typography>
            <Typography variant="caption" component="pre" sx={{ fontSize: '0.7rem' }}>
              Backend Status: {errorHandler.backendStatus}
              {'\n'}Last Health Check: {errorHandler.lastHealthCheck
                ? formatTime(errorHandler.lastHealthCheck, i18n.language)
                : 'Never'
              }
              {'\n'}Network Errors: {errorHandler.errors.filter(e => e.type === 'network').length}
            </Typography>
          </Box>
        </Collapse>
      )}
    </Alert>
  );
};

// Auto-mounting version that manages its own visibility
export const AutoBackendOfflineBanner: React.FC<Omit<BackendOfflineBannerProps, 'onDismiss'>> = (props) => {
  const errorHandler = useErrorHandler();
  
  // Auto-dismiss after successful reconnection
  React.useEffect(() => {
    if (errorHandler.backendStatus === 'online' && errorHandler.showOfflineBanner) {
      const timer = setTimeout(() => {
        errorHandler.dismissOfflineBanner();
      }, 2000); // Give user time to see the reconnection
      
      return () => clearTimeout(timer);
    }
  }, [errorHandler.backendStatus, errorHandler.showOfflineBanner]);

  return <BackendOfflineBanner {...props} />;
};